import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Clock, Database, HardDrive, KeyRound, LayoutDashboard, Lock, Users } from "lucide-react";

import PageChrome from "@/layouts/PageChrome.jsx";
import { Card, CardBody, CardExtra, CardHead, CardTitle } from "@/components/ui/Card.jsx";
import Button from "@/components/ui/Button.jsx";
import { Input, Label, Select } from "@/components/ui/Form.jsx";
import { Callout } from "@/components/ui/Display.jsx";
import Tag from "@/components/ui/Tag.jsx";
import { useConfirm, useToast } from "@/components/ui/feedback.js";
import SaveBar from "@/components/settings/SaveBar.jsx";
import {
    PickerRow,
    RevealField,
    SectionNav,
    ToggleRow,
} from "@/components/settings/SettingsPieces.jsx";

import { organizationService } from "@/lib/api.js";
import { ORGANIZATION_KEY, organizationQuery } from "@/lib/queries.js";
import { apiErrorMessage } from "@/lib/apiClient.js";
import { useAuth } from "@/context/auth.js";
import { formatDate } from "@/lib/format.js";
import {
    INTERVAL_OPTIONS,
    LIMITS,
    MODES,
    PRIVACY_TOGGLES,
    QUALITY_OPTIONS,
    RETENTION_PRESETS,
    SECTIONS,
    TRACKING_TOGGLES,
    changedLabels,
    dailyStoragePerUser,
    screenshotsPerDay,
    toForm,
    toPatch,
} from "@/lib/orgSettings.js";

/*
 * Replaces frontend/app/(dashboard)/organization/page.js.
 *
 * THE FIELDS ARE THE PAGE. The old one showed every value in a read-only
 * description list and put editing behind three separate "Update…" modals, so
 * changing the screenshot interval meant opening a dialog that also contained
 * fourteen unrelated fields, and you could not see the effect of a change next
 * to the thing it affects. Here every field is live and a save bar appears once
 * something is dirty, naming what is about to change.
 *
 * ONLY WHAT CHANGED IS SENT. That is newly safe: updateOrganization used to
 * replace the whole settings subdocument, deleting every key the request left
 * out — including both desktop gate passwords. See
 * backend/scripts/verify-org-settings-merge.js.
 */
const SECTION_ICONS = {
    org: Building2,
    tracking: Clock,
    privacy: Lock,
    dashboard: LayoutDashboard,
    retention: Database,
    passwords: KeyRound,
    storage: HardDrive,
};

export default function Settings() {
    const queryClient = useQueryClient();
    const toast = useToast();
    const confirm = useConfirm();
    const { user: me } = useAuth();

    const organizationId =
        typeof me?.organization === "object" ? me?.organization?._id : me?.organization;

    const orgQuery = useQuery(organizationQuery(organizationId));

    const organization = orgQuery.data?.organization ?? null;
    const storage = orgQuery.data?.storage ?? null;

    // updateOrganization is admin-only in the controller; a manager would see
    // an enabled form and a 403 on save.
    const canEdit = me?.role === "admin";

    if (!organization) {
        return (
            <>
                <PageChrome breadcrumb={["Settings"]} title="Organization Settings" />
                <main className="mx-auto w-full max-w-[1400px] p-4 lg:p-5">
                    <h1 className="text-[22px] font-semibold tracking-tight">
                        Organization Settings
                    </h1>
                    {orgQuery.isError ? (
                        <Callout tone="danger" className="mt-4" data-error>
                            {apiErrorMessage(orgQuery.error, "Could not load your organization.")}
                        </Callout>
                    ) : (
                        <p className="mt-4 text-[13px] text-slate-400 dark:text-slate-500">Loading…</p>
                    )}
                </main>
            </>
        );
    }

    return (
        <SettingsForm
            // Keyed by the saved document: a successful save, or someone else's
            // change arriving on a refetch, resets the form to the server's
            // truth rather than leaving stale edits on top of it.
            key={organization.updatedAt ?? organization._id}
            organization={organization}
            storage={storage}
            canEdit={canEdit}
            queryClient={queryClient}
            organizationId={organizationId}
            toast={toast}
            confirm={confirm}
        />
    );
}

function SettingsForm({
    organization,
    storage,
    canEdit,
    queryClient,
    organizationId,
    toast,
    confirm,
}) {
    const [values, setValues] = useState(() => toForm(organization));
    const [active, setActive] = useState("org");
    const sectionRefs = useRef({});

    const patch = useMemo(() => toPatch(values, organization), [values, organization]);
    const labels = useMemo(() => changedLabels(patch), [patch]);
    const dirty = labels.length > 0;

    const set = (key, value) => setValues((v) => ({ ...v, [key]: value }));

    /*
     * Highlight the section the reader is in. An IntersectionObserver rather
     * than a scroll handler, so the browser does the work and the callback
     * fires only when a boundary is actually crossed.
     */
    useEffect(() => {
        const seen = new Map();
        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) seen.set(entry.target.id, entry.intersectionRatio);
                const best = [...seen.entries()].sort((a, b) => b[1] - a[1])[0];
                if (best && best[1] > 0) setActive(best[0].replace("sec-", ""));
            },
            { rootMargin: "-96px 0px -55% 0px", threshold: [0, 0.25, 0.5, 1] },
        );

        for (const el of Object.values(sectionRefs.current)) if (el) observer.observe(el);
        return () => observer.disconnect();
    }, []);

    /* ---- Warn before leaving with unsaved edits ---- */
    useEffect(() => {
        if (!dirty) return;
        const onBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => window.removeEventListener("beforeunload", onBeforeUnload);
    }, [dirty]);

    const save = useMutation({
        mutationFn: (body) => organizationService.updateOrganization(organizationId, body),
        onSuccess: () => {
            toast("Organization settings saved");
            queryClient.invalidateQueries({ queryKey: ORGANIZATION_KEY(organizationId) });
        },
        onError: (error) => toast(apiErrorMessage(error, "Could not save the settings."), "error"),
    });

    /*
     * Turning any of these ON asks first. All three change what is collected
     * from people who are not in the room, and are switched by one person for
     * everyone — which is when a confirmation earns its place. Turning them OFF
     * never asks: reducing what you collect should be frictionless.
     */
    const setGated = async (field, next) => {
        if (next && field.confirm) {
            const ok = await confirm({ tone: "warning", ...field.confirm });
            if (!ok) return;
        }
        set(field.key, next);
    };

    const setMode = async (key) => {
        const mode = MODES.find((m) => m.key === key);
        if (key !== values.trackMode && mode?.confirm) {
            const ok = await confirm({ tone: "warning", ...mode.confirm });
            if (!ok) return;
        }
        set("trackMode", key);
    };

    const shotsPerDay = screenshotsPerDay(values.screenshotInterval);
    const mbPerDay = dailyStoragePerUser(values.screenshotInterval, values.screenshotResolution);

    const section = (id, title, children, extra) => {
        const Icon = SECTION_ICONS[id];
        return (
            <Card
                id={`sec-${id}`}
                ref={(el) => {
                    sectionRefs.current[id] = el;
                }}
                className="scroll-mt-20"
                data-section={id}
            >
                <CardHead>
                    <CardTitle icon={<Icon className="size-4 text-slate-400" strokeWidth={2} />}>
                        {title}
                    </CardTitle>
                    {extra && <CardExtra>{extra}</CardExtra>}
                </CardHead>
                {children}
            </Card>
        );
    };

    return (
        <>
            <PageChrome breadcrumb={["Settings"]} title="Organization Settings" />

            <main className="mx-auto flex w-full max-w-[1400px] flex-col gap-5 p-4 pb-24 lg:p-5 lg:pb-24">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <h1 className="text-[22px] font-semibold tracking-tight">
                            Organization Settings
                        </h1>
                        <p className="mt-0.5 text-[13px] text-slate-500 dark:text-slate-400">
                            Everything here is the organization-wide default. Individual people can
                            be overridden from their own page.
                        </p>
                    </div>
                    <Button to="/users">
                        <Users className="size-4" strokeWidth={2} />
                        Per-user overrides
                    </Button>
                </div>

                {!canEdit && (
                    <Callout tone="info" data-readonly>
                        You can see these settings but not change them — only an administrator can
                        save organization settings.
                    </Callout>
                )}

                <div className="grid grid-cols-1 gap-5 lg:grid-cols-[210px_minmax(0,1fr)]">
                    <SectionNav sections={SECTIONS} active={active} />

                    <div className="flex min-w-0 flex-col gap-5">
                        {/* ---------- Organization ---------- */}
                        {section(
                            "org",
                            "Organization",
                            <CardBody className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div>
                                    <Label htmlFor="orgName">Organization name</Label>
                                    <Input
                                        id="orgName"
                                        data-field="name"
                                        size="lg"
                                        disabled={!canEdit}
                                        value={values.name}
                                        onChange={(e) => set("name", e.target.value)}
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="orgIdentifier">Identifier</Label>
                                    <Input
                                        id="orgIdentifier"
                                        size="lg"
                                        className="font-mono"
                                        disabled
                                        value={organization.identifier ?? ""}
                                        readOnly
                                    />
                                    <p className="hint">
                                        Employees type this into the desktop app. It cannot be
                                        changed after registration.
                                    </p>
                                </div>
                                <div>
                                    <p className="label">Created</p>
                                    <p className="flex h-11 items-center text-[14px] text-slate-500 dark:text-slate-400">
                                        {organization.createdAt
                                            ? formatDate(organization.createdAt)
                                            : "—"}
                                    </p>
                                </div>
                            </CardBody>,
                        )}

                        {/* ---------- Tracking ---------- */}
                        {section(
                            "tracking",
                            "Tracking",
                            <CardBody className="space-y-5">
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                                    <div>
                                        <Label htmlFor="setInterval">Screenshot interval</Label>
                                        <Select
                                            id="setInterval"
                                            data-field="screenshotInterval"
                                            size="lg"
                                            disabled={!canEdit}
                                            value={values.screenshotInterval}
                                            onChange={(e) => set("screenshotInterval", e.target.value)}
                                        >
                                            {INTERVAL_OPTIONS.map(([value, label]) => (
                                                <option key={value} value={value}>
                                                    {label}
                                                </option>
                                            ))}
                                        </Select>
                                        <p className="hint" data-hint="interval">
                                            About {shotsPerDay} screenshots per person over an
                                            eight-hour day.
                                        </p>
                                    </div>

                                    <div>
                                        <Label htmlFor="setQuality">Screenshot quality</Label>
                                        <Select
                                            id="setQuality"
                                            data-field="screenshotResolution"
                                            size="lg"
                                            disabled={!canEdit}
                                            value={values.screenshotResolution}
                                            onChange={(e) =>
                                                set("screenshotResolution", e.target.value)
                                            }
                                        >
                                            {QUALITY_OPTIONS.map(([value, label]) => (
                                                <option key={value} value={value}>
                                                    {label}
                                                </option>
                                            ))}
                                        </Select>
                                        <p className="hint" data-hint="quality">
                                            Roughly {mbPerDay.toFixed(0)} MB per person per day at
                                            this interval.
                                        </p>
                                    </div>

                                    <div>
                                        <Label htmlFor="setIdle">Idle threshold</Label>
                                        <div className="flex items-center gap-2">
                                            <Input
                                                id="setIdle"
                                                data-field="idleThresholdMinutes"
                                                size="lg"
                                                type="number"
                                                step="0.5"
                                                min={LIMITS.idleMinutesMin}
                                                max={LIMITS.idleMinutesMax}
                                                disabled={!canEdit}
                                                value={values.idleThresholdMinutes}
                                                onChange={(e) =>
                                                    set(
                                                        "idleThresholdMinutes",
                                                        Number(e.target.value),
                                                    )
                                                }
                                            />
                                            <span className="text-[13px] whitespace-nowrap text-slate-500 dark:text-slate-400">
                                                minutes
                                            </span>
                                        </div>
                                        <p className="hint">
                                            No keyboard or mouse for this long counts as idle. Halves
                                            are allowed — the default is 1.5.
                                        </p>
                                    </div>
                                </div>

                                <div className="space-y-2.5">
                                    {TRACKING_TOGGLES.map((field) => (
                                        <ToggleRow
                                            key={field.key}
                                            field={field}
                                            checked={values[field.key]}
                                            locked={!canEdit}
                                            onChange={(next) => set(field.key, next)}
                                        />
                                    ))}
                                </div>
                            </CardBody>,
                        )}

                        {/* ---------- Privacy ---------- */}
                        {section(
                            "privacy",
                            "Privacy & visibility",
                            <CardBody className="space-y-5">
                                <div>
                                    <p className="label">Tracking mode</p>
                                    <PickerRow
                                        name="mode"
                                        options={MODES}
                                        value={values.trackMode}
                                        onChange={canEdit ? setMode : () => {}}
                                    />
                                </div>

                                <div className="space-y-2.5">
                                    {PRIVACY_TOGGLES.map((field) => (
                                        <ToggleRow
                                            key={field.key}
                                            field={field}
                                            checked={values[field.key]}
                                            locked={
                                                !canEdit ||
                                                (field.gate && !organization[field.gate])
                                            }
                                            onChange={(next) => setGated(field, next)}
                                        />
                                    ))}
                                </div>
                            </CardBody>,
                            <span className="text-xs text-slate-400 dark:text-slate-500">
                                Tell your team what you collect
                            </span>,
                        )}

                        {/* ---------- Dashboard ---------- */}
                        {section(
                            "dashboard",
                            "Dashboard",
                            <CardBody className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div>
                                    <Label htmlFor="setLate">Late start cutoff</Label>
                                    <Input
                                        id="setLate"
                                        data-field="lateStartTime"
                                        size="lg"
                                        type="time"
                                        disabled={!canEdit}
                                        value={values.lateStartTime}
                                        onChange={(e) => set("lateStartTime", e.target.value)}
                                    />
                                    <p className="hint">
                                        A first time entry after this counts as a late start on the
                                        dashboard.
                                    </p>
                                </div>
                                <div className="flex items-end">
                                    <p className="rounded-lg border border-slate-200 px-3.5 py-2.5 text-[12.5px] text-slate-500 dark:border-slate-800 dark:text-slate-400">
                                        Display only — this does not change when the desktop app
                                        records, only who the dashboard marks as late.
                                    </p>
                                </div>
                            </CardBody>,
                        )}

                        {/* ---------- Retention ---------- */}
                        {section(
                            "retention",
                            "Data retention",
                            <CardBody className="space-y-4">
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <div>
                                        <Label htmlFor="setRetention">Keep data for</Label>
                                        <div className="flex items-center gap-2">
                                            <Input
                                                id="setRetention"
                                                data-field="dataRetentionDays"
                                                size="lg"
                                                type="number"
                                                min={LIMITS.retentionMin}
                                                max={LIMITS.retentionMax}
                                                disabled={!canEdit}
                                                value={values.dataRetentionDays}
                                                onChange={(e) =>
                                                    set("dataRetentionDays", Number(e.target.value))
                                                }
                                            />
                                            <span className="text-[13px] whitespace-nowrap text-slate-500 dark:text-slate-400">
                                                days
                                            </span>
                                        </div>
                                        {/* The schema's own ceiling. html-markup
                                            offered up to 3650 days, which the
                                            save would have refused with a bare
                                            validation error. */}
                                        <p className="hint">
                                            Between {LIMITS.retentionMin} and {LIMITS.retentionMax}{" "}
                                            days.
                                        </p>
                                    </div>
                                    <div className="flex items-end">
                                        <div className="flex flex-wrap gap-1.5 pb-1">
                                            {RETENTION_PRESETS.map((days) => (
                                                <button
                                                    key={days}
                                                    type="button"
                                                    disabled={!canEdit}
                                                    data-preset={days}
                                                    onClick={() => set("dataRetentionDays", days)}
                                                    className={
                                                        Number(values.dataRetentionDays) === days
                                                            ? "rounded-md border border-brand-600 bg-brand-600 px-2.5 py-1.5 text-xs font-semibold text-white"
                                                            : "rounded-md border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 transition hover:border-slate-300 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300"
                                                    }
                                                >
                                                    {days}d
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <Callout tone="danger" data-retention-warning>
                                    <p className="text-[12.5px] leading-relaxed">
                                        A nightly job permanently deletes screenshots, activity and
                                        time entries older than this — including the stored image
                                        files.{" "}
                                        {Number(values.dataRetentionDays) <
                                            Number(organization.settings?.dataRetentionDays ?? 90) && (
                                            <strong className="font-semibold">
                                                Lowering this from{" "}
                                                {organization.settings?.dataRetentionDays ?? 90} to{" "}
                                                {values.dataRetentionDays} days will delete data you
                                                still have.{" "}
                                            </strong>
                                        )}
                                        Deletion cannot be undone, and takes effect on the next run.
                                    </p>
                                </Callout>
                            </CardBody>,
                        )}

                        {/* ---------- Desktop passwords ---------- */}
                        {section(
                            "passwords",
                            "Desktop app passwords",
                            <CardBody className="space-y-4">
                                <p className="text-[12.5px] leading-relaxed text-slate-500 dark:text-slate-400">
                                    Gate two actions inside the desktop tracker. The app fetches
                                    these on every settings sync, so a change reaches devices within
                                    about a minute.
                                </p>
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <div>
                                        <Label htmlFor="setInfoPw">Tracking info password</Label>
                                        <RevealField
                                            id="setInfoPw"
                                            value={values.trackingInfoPassword}
                                            onChange={(v) => set("trackingInfoPassword", v)}
                                        />
                                        <p className="hint">
                                            Opens the tracking-info panel on the device.
                                        </p>
                                    </div>
                                    <div>
                                        <Label htmlFor="setLogoutPw">Logout password</Label>
                                        <RevealField
                                            id="setLogoutPw"
                                            value={values.logoutPassword}
                                            onChange={(v) => set("logoutPassword", v)}
                                        />
                                        <p className="hint">
                                            Required before someone can sign out of the tracker.
                                        </p>
                                    </div>
                                </div>
                            </CardBody>,
                        )}

                        {/* ---------- Storage ---------- */}
                        {section(
                            "storage",
                            "Screenshot storage",
                            <CardBody className="space-y-4">
                                {/*
                                 * READ-ONLY, and deliberately so. html-markup
                                 * offered "our servers / your own S3 bucket" as
                                 * a choice, but local vs S3 is the server's
                                 * STORAGE_TYPE — there is no per-organization
                                 * switch to save. What an organization CAN do is
                                 * supply its own bucket, which is then used
                                 * inside S3 mode in preference to the platform's.
                                 */}
                                <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                                    <div>
                                        <dt className="dt">Mode</dt>
                                        <dd className="dd" data-storage-type>
                                            {storage?.type === "s3"
                                                ? "Amazon S3"
                                                : "Application server"}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="dt">Bucket</dt>
                                        <dd className="dd font-mono" data-storage-bucket>
                                            {storage?.bucketName ?? "—"}
                                        </dd>
                                    </div>
                                    <div>
                                        <dt className="dt">Region</dt>
                                        <dd className="dd font-mono">{storage?.region ?? "—"}</dd>
                                    </div>
                                </dl>

                                <Callout tone="info">
                                    <p className="text-[12.5px] leading-relaxed">
                                        Whether screenshots go to the application server or to
                                        Amazon S3 is a platform-wide setting, not something an
                                        organization chooses — so it is shown here rather than
                                        offered as a switch. What you can change is the bucket:{" "}
                                        {storage?.source === "organization" ? (
                                            <>
                                                this organization uses{" "}
                                                <strong className="font-semibold">its own</strong>{" "}
                                                bucket.
                                            </>
                                        ) : (
                                            <>
                                                this organization currently uses the{" "}
                                                <strong className="font-semibold">platform's</strong>{" "}
                                                storage.
                                            </>
                                        )}{" "}
                                        Credentials are set by an administrator and are never
                                        returned to anyone else.
                                    </p>
                                </Callout>
                            </CardBody>,
                            <Tag tone={storage?.source === "organization" ? "brand" : "slate"}>
                                {storage?.source === "organization" ? "Own bucket" : "Platform"}
                            </Tag>,
                        )}
                    </div>
                </div>
            </main>

            {canEdit && (
                <SaveBar
                    open={dirty}
                    labels={labels}
                    saving={save.isPending}
                    onSave={() => save.mutate(patch)}
                    onDiscard={() => setValues(toForm(organization))}
                />
            )}
        </>
    );
}
